import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { getArticleById, getRelatedArticles, categories } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-14 text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-500 mb-4">文章未找到</p>
          <Link
            to="/"
            className="px-5 py-2 bg-primary-500 text-background-50 rounded-lg text-sm hover:bg-primary-600 transition-colors cursor-pointer inline-block"
          >
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedArticles = getRelatedArticles(article);
  const currentCategory = categories.find((c) => c.slug === article.categorySlug);

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          image: article.imageUrl,
          datePublished: article.publishDate,
          author: {
            '@type': 'Person',
            name: article.author,
          },
          publisher: {
            '@type': 'Organization',
            name: '爆料网',
            logo: {
              '@type': 'ImageObject',
              url: 'https://www.baoliao.com/logo.png',
            },
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `https://www.baoliao.com/article/${article.id}`,
          },
        }}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: article.title,
          description: article.excerpt,
          url: `https://www.baoliao.com/article/${article.id}`,
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-14">
          <article className="max-w-4xl mx-auto px-4 md:px-6">
            {/* Breadcrumb */}
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  ...(currentCategory
                    ? [{ label: currentCategory.name, href: `/category/${article.categorySlug}` }]
                    : []),
                  { label: article.title },
                ]}
              />
            </div>

            {/* Article Header */}
            <header className="mb-8">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="px-3 py-1 text-xs font-medium bg-primary-100 text-primary-600 rounded-full hover:bg-primary-200 transition-colors cursor-pointer"
                >
                  {article.category}
                </Link>
                {article.isHot && (
                  <span className="px-3 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full">
                    热门
                  </span>
                )}
                {article.isNew && (
                  <span className="px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-full">
                    最新
                  </span>
                )}
              </div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.viewCount > 10000
                    ? `${(article.viewCount / 10000).toFixed(1)}万 阅读`
                    : `${article.viewCount} 阅读`}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-chat-3-line"></i>
                  {article.commentCount} 评论
                </span>
              </div>
            </header>

            {/* Featured Image */}
            <div className="mb-8 rounded-xl overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-auto object-cover max-h-[500px]"
                title={`爆料网 - ${article.title}`}
              />
            </div>

            {/* Article Content */}
            <div
              className="prose prose-base max-w-none mb-10 text-foreground-800 leading-relaxed
                [&>p]:mb-4 [&>p]:text-foreground-700 [&>p]:leading-loose
                [&>h2]:text-xl [&>h2]:font-heading [&>h2]:font-bold [&>h2]:text-foreground-950 [&>h2]:mt-8 [&>h2]:mb-4
                [&>h3]:text-lg [&>h3]:font-heading [&>h3]:font-semibold [&>h3]:text-foreground-900 [&>h3]:mt-6 [&>h3]:mb-3
                [&>strong]:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            <div className="flex items-center gap-2 mb-8 flex-wrap">
              <span className="text-sm text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-xs rounded-full bg-background-100 text-foreground-600 border border-background-200/70"
                >
                  #{tag}
                </span>
              ))}
            </div>

            {/* Share */}
            <div className="flex items-center gap-3 py-6 border-t border-background-200/70 mb-10">
              <span className="text-sm text-foreground-500">分享到：</span>
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微信">
                <i className="ri-wechat-line"></i>
              </a>
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微博">
                <i className="ri-weibo-line"></i>
              </a>
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="复制链接">
                <i className="ri-link"></i>
              </a>
            </div>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <section className="mb-10">
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-5 flex items-center gap-2">
                  <i className="ri-links-line text-primary-500"></i>
                  相关爆料
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {relatedArticles.map((related) => (
                    <ArticleCard key={related.id} article={related} variant="compact" />
                  ))}
                </div>
              </section>
            )}

            {/* Back to Home */}
            <div className="text-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                返回首页查看更多爆料
              </Link>
            </div>
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}